import React, { useState, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Slider from '@material-ui/core/Slider';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { usePortalAccess } from '../../../../hooks/usePortalAccess';
import { useSoundAlerts } from '../../../../context/SoundAlertsContext';
import { SOUND_EVENTS, CHIMES } from '../../../../services/sounds/registry';

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 800 },
  heading: { fontWeight: 600 },
  introduction: { marginTop: theme.spacing(1), marginBottom: theme.spacing(3) },
  panel: { overflow: 'hidden' },
  controls: { padding: theme.spacing(2, 3) },
  notice: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
  },
  warning: { borderLeftColor: theme.palette.warning.main },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(160px, 1fr) minmax(140px, 190px) auto',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      padding: theme.spacing(2),
    },
  },
  eventToggle: {
    marginLeft: -theme.spacing(1),
    [theme.breakpoints.down('xs')]: { gridColumn: '1 / -1' },
  },
  volume: { maxWidth: 340, marginTop: theme.spacing(2) },
  browser: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  help: {
    marginTop: theme.spacing(3),
    color: theme.palette.text.secondary,
    '& summary': { cursor: 'pointer', fontSize: theme.typography.body2.fontSize },
    '& p': { marginTop: theme.spacing(1) },
  },
  skeleton: {
    height: 58,
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
}));

function SoundAlertsSettings() {
  const classes = useStyles();
  const {
    preferences, status, error, saveError, dirty, saving, canSave, activated,
    playbackStatus, monitoringStatus, update, save, reload, activate, preview,
  } = useSoundAlerts();
  const [actionError, setActionError] = useState('');
  const [activating, setActivating] = useState(false);
  // Revalidate on settings activation; retain drafts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);
  const unavailable = status === 'unavailable';
  const editable = Boolean(preferences) && (status === 'ready' || unavailable);
  const message = (value) => (
    typeof value === 'string' ? value : (value && value.message) || 'Please try again.'
  );
  const run = async (action) => {
    setActionError('');
    try {
      await action();
    } catch (failure) {
      setActionError(message(failure));
    }
  };
  const enable = () => run(async () => {
    setActivating(true);
    try {
      await activate();
    } finally {
      setActivating(false);
    }
  });

  return (
    <section className={classes.root} aria-labelledby="sound-alerts-title">
      <Typography variant="h6" component="h2" id="sound-alerts-title" className={classes.heading}>
        Sound alerts
      </Typography>
      <Typography variant="body2" color="textSecondary" role="status">
        {{
          inactive: 'Enable sounds for live playback in this browser.',
          standby: 'Another activated tab handles live sounds.',
          starting: 'Starting live alerts…',
          live: 'Monitoring all authorized businesses',
          error: 'Live alerts interrupted. Reconnecting silently…',
          'evaluator-stale': 'Alert evaluation is delayed. Live sounds paused.',
          'coordination-unavailable': 'Browser tab coordination unavailable. Live sounds disabled.',
          'dedup-full': 'Alert burst limit reached. Excess sounds suppressed.',
          forbidden: 'Dispatcher access unavailable.',
        }[monitoringStatus]}
      </Typography>

      {unavailable && (
        <div className={classes.notice} role="status">
          <Typography variant="subtitle2">Preview only</Typography>
          <Typography variant="body2">
            Account saving and live alerts are unavailable. Selections are unsaved;
            only chime previews work.
          </Typography>
        </div>
      )}
      {((error && error !== saveError) || actionError || status === 'error') && (
        <div className={`${classes.notice} ${classes.warning}`} role="alert">
          <Typography variant="body2">
            {actionError || (error ? message(error) : 'Sound settings could not be loaded.')}
          </Typography>
          <Button size="small" color="primary" onClick={() => run(reload)}>Retry</Button>
        </div>
      )}
      {playbackStatus === 'blocked' && (
        <div className={`${classes.notice} ${classes.warning}`} role="alert">
          <Typography variant="body2">
            This browser blocked sound playback. Select Enable sounds and allow playback
            in your browser, then try a preview again.
          </Typography>
        </div>
      )}

      {status === 'loading' ? (
        <Box aria-busy="true" aria-label="Loading sound settings">
          <Typography variant="body2" color="textSecondary">Loading sound settings…</Typography>
          {[0, 1, 2, 3].map((id) => <div key={id} className={classes.skeleton} />)}
        </Box>
      ) : preferences ? (
        <Paper variant="outlined" className={classes.panel}>
          <div className={classes.controls}>
            <FormControlLabel
              control={(
                <Switch
                  color="primary"
                  checked={!preferences.masterEnabled}
                  disabled={!editable || saving}
                  onChange={(event) => update({ masterEnabled: !event.target.checked })}
                />
              )}
              label="Mute all sounds"
            />
            <Typography variant="body2" color="textSecondary">
              Event choices are kept when all sounds are muted.
            </Typography>
            <div className={classes.volume}>
              <Typography variant="body2" id="sound-volume-label">
                Volume · {Math.round(preferences.volume * 100)}%
              </Typography>
              <Slider
                aria-labelledby="sound-volume-label"
                getAriaValueText={(value) => `${value}%`}
                value={Math.round(preferences.volume * 100)}
                min={0}
                max={100}
                step={1}
                valueLabelDisplay="auto"
                disabled={!editable || saving}
                onChange={(event, value) => update({ volume: value / 100 })}
              />
            </div>
            <div className={classes.browser}>
              <div>
                <Typography variant="body2">Playback in this browser</Typography>
                <Typography variant="caption" color="textSecondary" role="status">
                  {playbackStatus === 'blocked' ? 'Blocked by browser'
                    : activated && playbackStatus === 'ready' ? 'Ready for sound playback'
                      : 'Select Enable sounds or Preview'}
                </Typography>
              </div>
              <Button
                variant="outlined"
                color="primary"
                disabled={!editable || activating}
                onClick={enable}
              >
                {activating ? 'Enabling…' : 'Enable sounds'}
              </Button>
            </div>
          </div>
          {SOUND_EVENTS.map((soundEvent) => {
            const eventPreferences = preferences.events[soundEvent.id] || {
              enabled: true, chime: soundEvent.defaultChime,
            };
            const labelId = `sound-chime-${soundEvent.id}`;
            return (
              <div className={classes.row} key={soundEvent.id}>
                <FormControlLabel
                  className={classes.eventToggle}
                  control={(
                    <Switch
                      color="primary"
                      checked={eventPreferences.enabled}
                      disabled={!editable || saving}
                      onChange={(event) => update({
                        events: { [soundEvent.id]: { enabled: event.target.checked } },
                      })}
                    />
                  )}
                  label={<Typography variant="body2">{soundEvent.label}</Typography>}
                />
                <FormControl variant="outlined" size="small" disabled={!editable || saving}>
                  <InputLabel id={labelId}>Chime</InputLabel>
                  <Select
                    labelId={labelId}
                    id={`${labelId}-select`}
                    label="Chime"
                    inputProps={{ 'aria-label': `${soundEvent.label} chime` }}
                    value={eventPreferences.chime}
                    onChange={(event) => update({
                      events: { [soundEvent.id]: { chime: event.target.value } },
                    })}
                  >
                    {CHIMES.map((chime) => (
                      <MenuItem key={chime.id} value={chime.id}>{chime.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  color="primary"
                  aria-label={`Preview ${soundEvent.label}`}
                  disabled={!editable}
                  onClick={() => run(() => preview(eventPreferences.chime))}
                >
                  Preview
                </Button>
              </div>
            );
          })}
        </Paper>
      ) : status !== 'error' && (
        <div className={classes.notice}>
          <Typography variant="body2">No sound settings are available yet.</Typography>
          <Button size="small" color="primary" onClick={() => run(reload)}>Try again</Button>
        </div>
      )}

      {preferences && status !== 'loading' && (
        <div className={classes.footer}>
          {saveError && (
            <Typography variant="body2" color="error" role="alert">
              {saveError}
              {!canSave && <Button size="small" color="primary" onClick={() => run(reload)}>Retry</Button>}
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary" role="status">
            {unavailable ? 'Preview only · selections are not saved'
              : saving ? 'Saving settings…'
                : dirty ? 'You have unsaved changes' : 'No unsaved changes'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={!canSave || !dirty || saving || !editable || unavailable}
            onClick={() => run(save)}
          >
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      )}
      <details className={classes.help}>
        <summary>About browser sound alerts</summary>
        <Typography variant="body2">
          Settings follow your account. En route — next assignment means one accepted
          unfinished delivery and no second assignment; it does not mean the driver is idle.
          Previews play on request even when live alerts are muted.
          Browser permission is separate from account settings. Sound is not guaranteed
          when a background tab is suspended, the browser is closed, or the device is asleep.
        </Typography>
      </details>
    </section>
  );
}

export function NotificationsSection() {
  const { dispatcher } = usePortalAccess();
  if (!dispatcher) {
    return (
      <Typography variant="body2" color="textSecondary">
        Sound alert settings are available to dispatchers only.
      </Typography>
    );
  }
  return <SoundAlertsSettings />;
}
